import { useState } from 'react';
import { motion } from 'framer-motion';
import { useRouter } from 'next/navigation';
import { useAppSelector } from '@/app/store/hooks';
import { ChevronRight, Play } from 'lucide-react';
import dynamic from 'next/dynamic';

const LoginPopover = dynamic(
    () => import('@/components/auth/login-popover').then((mod) => mod.LoginPopover),
    { ssr: false }
);


const Hero = ({ isLoading }: { isLoading?: boolean }) => {
    const router = useRouter();
    const token = useAppSelector((state) => state.auth.token);
    const isAuthenticated = !!token;
    const [isLoginPopoverOpen, setIsLoginPopoverOpen] = useState(false);

    const handleStartFree = () => {
        if (isAuthenticated) {
            router.push('/create/build-a-new-presentation/company-details');
        } else {
            setIsLoginPopoverOpen(true);
        }
    };

    return (
        <section
            className="min-h-screen flex flex-col items-center justify-center px-4 pt-32 md:pt-0 relative overflow-hidden bg-white"
        >
            {/* Background Elements */}
            {/* Subtle Grid Pattern - Adjusted for Light Mode */}
            <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px] pointer-events-none" />
            <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:120px_120px] pointer-events-none opacity-80" />

            {/* Vignette for focus */}
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_0%,white_90%)] pointer-events-none" />


            {/* Creative Editor Toolbar Doodle - Floating Bottom Left (desktop only) */}
            <motion.div
                className="absolute bottom-[4%] left-[10%] bg-white/80 backdrop-blur-xl rounded-full px-5 py-2.5 hidden md:flex items-center gap-5 shadow-2xl shadow-blue-900/10 border border-gray-100 -rotate-6 z-0"
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1, rotate: [-2, -4, -6] }}
                transition={{ duration: 1.2, rotate: { duration: 8, repeat: Infinity, ease: "easeInOut" } }}
            >
                <div className="flex items-center gap-2.5 border-r border-gray-200 pr-4">
                    <span className="font-syne text-gray-800 text-sm font-bold">Poppins</span>
                    <span className="font-jakarta text-gray-500 text-xs font-medium">40</span>
                </div>
                <div className="flex items-center gap-4 text-gray-600">
                    <span className="font-bold text-sm hover:text-blue-600 transition-colors cursor-default">B</span>
                    <div className="flex flex-col gap-0.5 w-3 cursor-default opacity-60">
                        <div className="h-0.5 bg-gray-800 w-full rounded-full" />
                        <div className="h-0.5 bg-gray-800 w-2/3 rounded-full" />
                        <div className="h-0.5 bg-gray-800 w-full rounded-full" />
                    </div>
                    <span className="font-serif italic text-lg hover:text-blue-600 transition-colors cursor-default">A</span>
                </div>
                {/* Magic Sparkle Connector */}
                <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center shadow-sm border border-blue-100">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 2L14.4 9.6L22 12L14.4 14.4L12 22L9.6 14.4L2 12L9.6 9.6L12 2Z" fill="#3B82F6" />
                    </svg>
                </div>
            </motion.div>

            {/* Text Selection Box Doodle - Floating Bottom Right (desktop only) */}
            <motion.div
                className="absolute bottom-[25%] right-[12%] z-0 hidden md:block"
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.5, duration: 0.8 }}
            >
                <div className="relative border-2 border-dashed border-blue-400/60 rounded-lg p-4 rotate-2 bg-blue-50/50">
                    {/* Corner Handles */}
                    <div className="absolute -top-1.5 -left-1.5 w-3 h-3 bg-white border border-blue-500 rounded-full shadow-sm" />
                    <div className="absolute -top-1.5 -right-1.5 w-3 h-3 bg-white border border-blue-500 rounded-full shadow-sm" />
                    <div className="absolute -bottom-1.5 -left-1.5 w-3 h-3 bg-white border border-blue-500 rounded-full shadow-sm" />
                    <div className="absolute -bottom-1.5 -right-1.5 w-3 h-3 bg-white border border-blue-500 rounded-full shadow-sm" />

                    {/* Ghost Text */}
                    <div className="opacity-60">
                        <div className="h-4 w-32 bg-blue-200/50 rounded mb-2" />
                        <div className="h-4 w-24 bg-blue-200/50 rounded" />
                    </div>
                </div>
            </motion.div>

            {/* Creative Background Doodles */}



            {/* Document Icon - Right Side (desktop only) */}
            <motion.svg
                className="absolute top-[20%] right-[10%] w-14 h-14 text-blue-200/50 rotate-6 hidden md:block"
                viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0, rotate: [6, 12, 6] }}
                transition={{ delay: 0.7, rotate: { duration: 6, repeat: Infinity, ease: "easeInOut" } }}
            >
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                <polyline points="14 2 14 8 20 8"></polyline>
                <line x1="16" y1="13" x2="8" y2="13"></line>
                <line x1="16" y1="17" x2="8" y2="17"></line>
                <polyline points="10 9 9 9 8 9"></polyline>
            </motion.svg>



            {/* Dotted Curved Line - Top Right (desktop only) */}
            <motion.svg
                className="absolute top-[15%] right-[20%] w-48 h-24 text-gray-300 hidden md:block"
                viewBox="0 0 100 50" fill="none"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.2 }}
            >
                <path d="M0 25 Q 50 0, 100 25" stroke="currentColor" strokeWidth="2" strokeDasharray="6 6" strokeLinecap="round" />
            </motion.svg>




            {/* Main Content Container - Animated Entrance */}
            <motion.div
                className="relative z-10 max-w-5xl mx-auto text-center flex flex-col items-center gap-6 mt-0 md:mt-12"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{
                    opacity: isLoading ? 0 : 1,
                    scale: isLoading ? 0.8 : 1
                }}
                transition={{
                    duration: 1.4,
                    ease: [0.25, 1, 0.5, 1], // Soft spring / Smooth Ease Out
                    delay: 0.2
                }}
            >

                {/* Sign up chip */}
                <button
                    onClick={handleStartFree}
                    className="cursor-pointer px-5 py-2 rounded-full text-sm font-medium font-jakarta text-blue-600 border border-blue-200 hover:bg-blue-50 transition-colors flex items-center gap-1.5"
                >
                    Sign up to get free credits
                    <ChevronRight size={16} />
                </button>

                {/* Typography */}
                <div className="space-y-4">

                    <h1 className="font-syne font-bold text-[32px] md:text-[56px] leading-[1.1] tracking-tight text-gray-900" style={{ textShadow: "0 0 40px rgba(59, 130, 246, 0.25)" }}>
                        Create <br className="md:hidden" />

                        <span className="relative inline-block mx-2 px-1">
                            <span className="relative z-10 text-blue-600 px-2" style={{ textShadow: "0 0 40px rgba(37,99,235,0.5)" }}>Professional Decks</span>
                            {/* Highlight Effect */}
                            <motion.span
                                className="absolute inset-0 bg-blue-50 transform -skew-x-3 rounded-md -z-10"
                                initial={{ scaleX: 0 }}
                                animate={{ scaleX: isLoading ? 0 : 1 }} // Sync with main entrance
                                transition={{ duration: 0.8, delay: 0.8, ease: "circOut" }}
                                style={{ originX: 0 }}
                            />
                        </span>

                        <br className="block" />
                        in <span className="relative inline-block px-2">
                            <span className="relative z-10 text-gray-900">Minutes</span>
                            <svg className="absolute -bottom-2 left-0 w-full h-3 text-blue-400" viewBox="0 0 100 10" preserveAspectRatio="none">
                                <path d="M0 5 Q 50 10, 100 5" stroke="currentColor" strokeWidth="3" fill="none" />
                            </svg>
                        </span> with AI
                    </h1>

                    <p className="font-jakarta text-gray-600 text-[14px] md:text-[16px] max-w-3xl mx-auto leading-relaxed font-medium">
                        Create professional presentations, pitch decks, business documents, and deep market research — all with AI, in minutes.
                    </p>
                </div>

                {/* CTA Buttons */}
                <div className="flex flex-col items-center gap-5 mt-6 md:mt-10">
                    <a
                        href="https://www.youtube.com/@DeckwaleAI"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-700 rounded-full font-semibold text-xs md:text-sm font-jakarta transition-all flex items-center gap-2 whitespace-nowrap"
                    >
                        <Play size={14} className="text-gray-500" />
                        Watch Demo
                    </a>

                    <button
                        onClick={() => router.push('/')}
                        className="group/btn cursor-pointer relative overflow-hidden px-8 py-3.5 md:px-12 md:py-4 rounded-full font-bold text-sm md:text-base font-jakarta text-white tracking-wide border border-[#111] active:translate-y-[2px] transition-transform whitespace-nowrap shadow-md"
                        style={{
                            background: 'linear-gradient(180deg, #555 0%, #222 49%, #000 51%, #222 100%)',
                            boxShadow: 'inset 0px 1px 0px rgba(255,255,255,0.5), 0px 4px 12px rgba(0,0,0,0.15)',
                            textShadow: '0px -1px 0px rgba(0,0,0,0.4)',
                            backfaceVisibility: 'hidden',
                        }}
                    >
                        <span
                            className="absolute inset-0 opacity-0 group-hover/btn:opacity-100 transition-opacity duration-500 pointer-events-none"
                            style={{
                                background: 'linear-gradient(105deg, transparent 30%, rgba(255,255,255,0.15) 45%, rgba(255,255,255,0.25) 50%, rgba(255,255,255,0.15) 55%, transparent 70%)',
                            }}
                        />
                        <span className="relative z-10 flex items-center gap-2">
                            Explore Deckwale<ChevronRight size={18} />
                        </span>
                    </button>
                    
                    <p className="text-xs text-gray-400 font-jakarta font-medium mt-1">
                        No credit card required
                    </p>
                </div>

            </motion.div>
            <LoginPopover 
                isOpen={isLoginPopoverOpen} 
                onClose={() => setIsLoginPopoverOpen(false)} 
            />
        </section>
    );
};

export default Hero;
